import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { Lock, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import PasswordInput from '@/components/shared/PasswordInput';
import ThemeToggle from '@/components/shared/ThemeToggle';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setError('Missing reset token. Please request a new reset link.');
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6" data-testid="reset-password-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold mb-2" style={{fontFamily: "'DM Serif Display', serif"}}>
          <span className="text-slate-900 dark:text-white">Choose a new </span>
          <span className="text-emerald-600 dark:text-emerald-400">password</span>
        </h1>
        <p className="text-slate-500 dark:text-zinc-500 mb-8 text-sm">Pick something strong. You&apos;ll use it next time you sign in.</p>

        {done ? (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-5" data-testid="reset-success">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 mb-2" />
            <p className="text-emerald-700 dark:text-emerald-300 text-sm font-medium">Password updated</p>
            <p className="text-slate-500 dark:text-zinc-400 text-sm mt-1">Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="reset-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">New password</label>
              <PasswordInput
                value={password}
                onChange={e => setPassword(e.target.value)}
                testId="reset-password-input"
                placeholder="Min 6 characters"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">Confirm password</label>
              <PasswordInput
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                testId="reset-confirm-input"
                placeholder="Re-enter the same"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !token}
              data-testid="reset-submit-btn"
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-black font-medium py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
            </button>
            <Link to="/login" className="block text-center text-sm text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
